/**
 * Admin "Jobs" page — `/ops/jobs`.
 *
 * Lists recent bulk jobs (durable, queue-driven fan-out tasks submitted from
 * Manage venues / Manage series). Lets the admin click a row to re-open the
 * progress modal, see live status + failures, and cancel an in-flight job.
 *
 * Auto-refreshes the list every 5s while the page is open, so jobs that
 * finish in the background show their terminal status without a manual
 * refresh.
 *
 * Accessibility:
 * - One `<h1>`.
 * - The list is a real `<table>` with `<caption>` + `<th scope="col">`.
 * - Status badges convey state via text label (not color alone).
 * - The progress modal (`BulkJobModal`) is reused as-is.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import BulkJobModal from '../../components/BulkJobModal';
import {
  isTerminalStatus,
  listJobs,
  type BulkJob,
  type JobKind,
  type JobStatus,
} from '../../lib/jobs';

const REFRESH_MS = 5000;

const KIND_LABELS: Record<JobKind, string> = {
  'venue-scan-ig': 'Venue: scan IG',
  'venue-scan-website': 'Venue: scan website',
  'venue-patch': 'Venue: bulk patch',
  'series-scan-ig': 'Series: scan IG',
  'venue-discover-from-events': 'Venue: discover from events',
  'recalc-date': 'Recalculate date',
  'website-source-update-promote': 'Website: apply reviewed capture',
  'website-source-capture': 'Website: capture page',
};

const STATUS_LABELS: Record<JobStatus, { label: string; cls: string }> = {
  queued: { label: 'Queued', cls: 'bg-secondary' },
  running: { label: 'Running', cls: 'bg-primary' },
  completed: { label: 'Completed', cls: 'bg-success' },
  'completed-with-errors': { label: 'Completed (errors)', cls: 'bg-warning text-dark' },
  cancelled: { label: 'Cancelled', cls: 'bg-secondary' },
  failed: { label: 'Failed', cls: 'bg-danger' },
};

const STATUS_OPTIONS: Array<JobStatus | 'all'> = [
  'all',
  'running',
  'queued',
  'completed',
  'completed-with-errors',
  'cancelled',
  'failed',
];

const KIND_OPTIONS: Array<JobKind | 'all'> = [
  'all',
  'venue-scan-ig',
  'venue-scan-website',
  'venue-patch',
  'series-scan-ig',
  'venue-discover-from-events',
  'recalc-date',
  'website-source-update-promote',
  'website-source-capture',
];

const LIMIT = 50;

export default function AdminJobsPage() {
  const [jobs, setJobs] = useState<BulkJob[] | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<JobStatus | 'all'>('all');
  const [kindFilter, setKindFilter] = useState<JobKind | 'all'>('all');
  const [active, setActive] = useState<{ jobPk: string; jobId: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await listJobs({
        kind: kindFilter === 'all' ? undefined : kindFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: LIMIT,
      });
      setJobs(r.items);
      setError(undefined);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, [kindFilter, statusFilter]);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => {
      void refresh();
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const counts = useMemo(() => {
    const out = { total: 0, running: 0, queued: 0 };
    for (const j of jobs ?? []) {
      out.total++;
      if (j.status === 'running') out.running++;
      else if (j.status === 'queued') out.queued++;
    }
    return out;
  }, [jobs]);

  return (
    <AdminLayout fluid>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h1 className="h3 mb-0 me-auto">Bulk jobs</h1>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => void refresh()}
        >
          Refresh
        </button>
      </div>

      <p className="text-muted small mb-3">
        Server-side fan-out tasks (bulk IG scans, bulk patches) submitted from{' '}
        <strong>Manage venues</strong> / <strong>Manage series</strong>. Jobs run on the
        API queue and survive tab close. Click a row to see live progress, recent
        failures, or cancel.
      </p>

      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div>
          <label htmlFor="job-status-filter" className="form-label small mb-1">
            Status
          </label>
          <select
            id="job-status-filter"
            className="form-select form-select-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as JobStatus | 'all')}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All statuses' : STATUS_LABELS[s].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="job-kind-filter" className="form-label small mb-1">
            Kind
          </label>
          <select
            id="job-kind-filter"
            className="form-select form-select-sm"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as JobKind | 'all')}
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {k === 'all' ? 'All kinds' : KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="text-muted small ms-auto" aria-live="polite">
          {jobs ? (
            <>
              Showing <strong>{counts.total}</strong> · {counts.running} running ·{' '}
              {counts.queued} queued
            </>
          ) : (
            'Loading…'
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-warning" role="alert">
          Could not load jobs: {error.message}
        </div>
      )}

      {jobs && jobs.length === 0 && !error && (
        <div className="text-muted small">
          No jobs match the current filters.
        </div>
      )}

      {jobs && jobs.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <caption className="visually-hidden">Recent bulk jobs</caption>
            <thead>
              <tr>
                <th scope="col">Status</th>
                <th scope="col">Kind</th>
                <th scope="col">Label</th>
                <th scope="col" className="text-end">Progress</th>
                <th scope="col">Created</th>
                <th scope="col">By</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <JobRow
                  key={`${j.partitionKey}/${j.rowKey}`}
                  job={j}
                  onOpen={() => setActive({ jobPk: j.partitionKey, jobId: j.rowKey })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BulkJobModal
        jobPk={active?.jobPk}
        jobId={active?.jobId}
        onClose={() => setActive(null)}
        onComplete={() => { void refresh(); }}
      />
    </AdminLayout>
  );
}

function JobRow({ job, onOpen }: { job: BulkJob; onOpen: () => void }) {
  const meta = STATUS_LABELS[job.status];
  const pct = job.total > 0 ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;
  const terminal = isTerminalStatus(job.status);
  const created = formatRelativeTime(job.createdAt);
  return (
    <tr>
      <td>
        <span className={`badge ${meta.cls}`}>{meta.label}</span>
      </td>
      <td className="small text-muted">{KIND_LABELS[job.kind] ?? job.kind}</td>
      <td>{job.label}</td>
      <td className="text-end small" style={{ minWidth: 140 }}>
        <span>
          {job.done} / {job.total}
        </span>
        {job.failed > 0 && (
          <span className="text-danger ms-2">({job.failed} failed)</span>
        )}
        <div
          className="progress mt-1"
          style={{ height: '0.35rem' }}
          aria-label={`Progress: ${pct}%`}
        >
          <div
            className={progressBarClass(job.status, job.failed)}
            role="progressbar"
            style={{ width: `${pct}%` }}
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </td>
      <td className="small text-muted" title={job.createdAt}>
        {created}
      </td>
      <td className="small text-muted">{job.createdBy ?? '—'}</td>
      <td className="text-end">
        <button
          type="button"
          className={`btn btn-sm ${terminal ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
          onClick={onOpen}
        >
          {terminal ? 'View' : 'Open'}
        </button>
      </td>
    </tr>
  );
}

function progressBarClass(status: JobStatus, failed: number): string {
  const base = 'progress-bar';
  if (status === 'failed') return `${base} bg-danger`;
  if (status === 'cancelled') return `${base} bg-secondary`;
  if (status === 'completed') return `${base} bg-success`;
  if (status === 'completed-with-errors') return `${base} bg-warning`;
  if (failed > 0) return `${base} bg-warning`;
  return `${base} progress-bar-striped progress-bar-animated`;
}

/** Human-friendly relative time, falls back to short ISO. */
function formatRelativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const deltaSec = Math.round((Date.now() - t) / 1000);
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const min = Math.round(deltaSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 14) return `${day}d ago`;
  return new Date(t).toISOString().slice(0, 10);
}
